// frontend/src/services/tourService.js
// Centralized tour management service

import api from './api';

// Tour IDs - used for tracking which tours the user has seen
export const TOUR_IDS = {
  HOME: 'home',
  MONTHLY_PLAN: 'monthly-plan',
  SPENDING_PLAN: 'spending-plan',
  ACTUAL_INCOME: 'actual-income',
  DAILY_DIGEST: 'daily-digest',
  HABITS: 'habits',
  GROUP_SAVINGS: 'group-savings',
  COMPETITIONS: 'competitions',
  SCENARIO_PLANNER: 'scenario-planner'
};

// Default Joyride styles for consistent look across all tours
export const defaultTourStyles = {
  options: {
    primaryColor: '#3B82F6', // blue-500
    textColor: '#1F2937', // slate-800
    backgroundColor: '#FFFFFF',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10000,
    arrowColor: '#FFFFFF'
  },
  buttonNext: {
    backgroundColor: '#3B82F6',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px'
  },
  buttonBack: {
    color: '#6B7280',
    marginRight: '8px'
  },
  buttonSkip: {
    color: '#9CA3AF'
  },
  tooltip: {
    borderRadius: '8px',
    padding: '16px'
  },
  tooltipTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '8px'
  },
  tooltipContent: {
    fontSize: '14px',
    lineHeight: '1.5'
  }
};

// Check if user has seen a specific tour
export const hasSeenTour = (tourId) => {
  try {
    const seenTours = JSON.parse(localStorage.getItem('seenTours') || '{}');
    return seenTours[tourId] === true;
  } catch {
    return false;
  }
};

// Mark a tour as seen
export const markTourAsSeen = async (tourId, token) => {
  try {
    // Save locally first
    const seenTours = JSON.parse(localStorage.getItem('seenTours') || '{}');
    seenTours[tourId] = true;
    localStorage.setItem('seenTours', JSON.stringify(seenTours));

    // Also save to backend (for persistence across devices)
    if (token) {
      try {
        await api.post('/api/onboarding/tour-seen', { tourId }, {
          headers: { 'x-auth-token': token }
        });
      } catch (err) {
        // Non-critical - local storage is sufficient
        console.log('Could not save tour status to backend:', err.message);
      }
    }
  } catch (err) {
    console.error('Error marking tour as seen:', err);
  }
};

// Reset all tours (for testing or user request)
export const resetAllTours = () => {
  localStorage.removeItem('seenTours');
};

// Reset a specific tour
export const resetTour = (tourId) => {
  try {
    const seenTours = JSON.parse(localStorage.getItem('seenTours') || '{}');
    delete seenTours[tourId];
    localStorage.setItem('seenTours', JSON.stringify(seenTours));
  } catch (err) {
    console.error('Error resetting tour:', err);
  }
};

// Load tour status from backend (call on login)
export const syncTourStatus = async (token) => {
  try {
    const response = await api.get('/api/onboarding/tour-status', {
      headers: { 'x-auth-token': token }
    });
    if (response.data && response.data.seenTours) {
      // Merge with local storage (backend takes precedence to ensure consistency)
      const localTours = JSON.parse(localStorage.getItem('seenTours') || '{}');
      const merged = { ...localTours, ...response.data.seenTours };
      localStorage.setItem('seenTours', JSON.stringify(merged));
    }
  } catch (err) {
    // Non-critical - local storage is sufficient
    console.log('Could not sync tour status from backend:', err.message);
  }
};
